import type { EncryptionMetadata } from '@/types'

const CHUNK_SIZE = 64 * 1024 * 1024 // 64MB

// --- Key management ---

export async function generateEncryptionKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  )
}

export async function exportKeyToString(key: CryptoKey): Promise<string> {
  const raw = await crypto.subtle.exportKey('raw', key)
  return base64urlEncode(new Uint8Array(raw))
}

export async function importKeyFromString(str: string): Promise<CryptoKey> {
  const raw = base64urlDecode(str)
  return crypto.subtle.importKey(
    'raw',
    raw.buffer as ArrayBuffer,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  )
}

// --- Encryption / Decryption ---

export async function encryptFile(
  file: File,
  key: CryptoKey,
  onProgress?: (percent: number) => void
): Promise<{ encryptedBlob: Blob; metadata: EncryptionMetadata }> {
  const chunks: Blob[] = []
  const chunkMetas: EncryptionMetadata['chunks'] = []
  const totalSize = file.size
  let offset = 0

  while (offset < totalSize) {
    const end = Math.min(offset + CHUNK_SIZE, totalSize)
    const slice = file.slice(offset, end)
    const plaintext = new Uint8Array(await slice.arrayBuffer())

    const ivBytes = crypto.getRandomValues(new Uint8Array(12))
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
      key,
      plaintext
    )

    chunks.push(new Blob([ciphertext]))
    chunkMetas.push({
      iv: base64urlEncode(ivBytes),
      byteLength: ciphertext.byteLength,
    })

    offset = end
    if (onProgress) {
      onProgress(Math.round((offset / totalSize) * 100))
    }
  }

  const metadata: EncryptionMetadata = {
    algorithm: 'AES-256-GCM',
    chunkSize: CHUNK_SIZE,
    chunks: chunkMetas,
    totalSize,
  }

  return { encryptedBlob: new Blob(chunks), metadata }
}

export async function decryptFile(
  blob: Blob,
  key: CryptoKey,
  metadata: EncryptionMetadata,
  onProgress?: (percent: number) => void
): Promise<Blob> {
  const decryptedChunks: Blob[] = []
  let offset = 0
  const totalEncryptedSize = metadata.chunks.reduce((sum, c) => sum + c.byteLength, 0)

  for (let i = 0; i < metadata.chunks.length; i++) {
    const chunkMeta = metadata.chunks[i]
    const ivBytes = base64urlDecode(chunkMeta.iv)
    const ciphertextSlice = blob.slice(offset, offset + chunkMeta.byteLength)
    const ciphertext = await ciphertextSlice.arrayBuffer()

    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
      key,
      ciphertext
    )

    decryptedChunks.push(new Blob([plaintext]))
    offset += chunkMeta.byteLength

    if (onProgress) {
      onProgress(Math.round((offset / totalEncryptedSize) * 100))
    }
  }

  return new Blob(decryptedChunks)
}

// --- URL fragment helpers ---

export function extractKeyFromFragment(): string | null {
  const hash = window.location.hash
  if (!hash) return null
  const match = hash.match(/[#&]key=([A-Za-z0-9_-]+)/)
  return match ? match[1] : null
}

export function buildEncryptedShareURL(baseURL: string, keyString: string): string {
  return `${baseURL}#key=${keyString}`
}

// --- Base64url helpers ---

export function base64urlEncode(data: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function base64urlDecode(str: string): Uint8Array {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
  while (base64.length % 4) {
    base64 += '='
  }
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}
